/**
 * Boot sequence (plan G6): capability check → game data → Rapier → renderer → environment → every
 * model and texture set in assets.json (progress on the start overlay; a failure names the asset) →
 * material library → post pipeline (camera far plane = env.far) → sim (with the asteroid field of the active
 * preset and the ship colliders of the loaded models) and player spawn → features → dev tools → loop.
 * The first preset comes from the saved settings (or `?quality=`), so the render stack and the field are built
 * once at the right quality. Error screens follow spec §12.
 * M2 (plan §2.4): the game mode (`?mode=` or app.json `defaultMode`; career switches the M1 waves and respawn off),
 * boot skips the deferred station and prop assets, and the context carries the mode, holds, the service registry,
 * the asset loader and the start override (a feature's replacement for "Click to fly").
 *
 * Game modes (M3 R1, plan §2.3, §2.5, D-TBD 3): `?mode=` or the build default (`career` since the M2 landing) picks `sandbox` (the M1 wave game,
 * unchanged) or `reach` (a sim with AI but no waves and no preset field, plus the reach runtime, created after the
 * ship views). Both modes skip the models of `deferredAssetPrefixes` at boot; the Reach loads them per system.
 *
 * M2 bridge seam (R22d, M3 plan §1.2.1, §7 items 1, 5 and 11): with `app.json careerInReach` on, the career runs in
 * the Reach (`ctx.reach` non-null) and `?mode=reach` is an alias of `career`. The Reach runtime then takes M2's
 * career and encounter director through its ports (`createReachBridge` in mode.ts, loaded with the runtime chunk).
 * One loader (`ctx.loader`) and one deferred filter (`isDeferredAsset`) serve both: boot skips every deferred
 * prefix, the career feature loads its `station.`/`prop.` subset and the Reach its `reach.` models per system.
 * R22k: the Reach then saves through the career (`createCareerReachWiring`: its store writes M2 autosaves, and the
 * `careerReach` service gives the career runtime its Reach link for docked loads and docked respawn).
 */
import { PerspectiveCamera, Scene } from 'three/webgpu'
import { AssetLoadError, countAssets, loadAllAssets } from '../assets/boot'
import { createAssetLoader } from '../assets/loader'
import { createShipStream, loadShipStreamConfig } from '../assets/shipStream'
import { standInModel } from '../assets/standIn'
import { logger } from '../core/logger'
import { loadGameData } from '../data'
import { createPerfOverlay } from '../dev/perfOverlay'
import { createGpuTiming } from '../dev/gpuTiming'
import { loadDevConfig } from '../dev/config'
import { createSimControl, type SimControl } from '../dev/simControl'
import { cameraTuning } from '../features/camera/tuning'
import { hudTuning } from '../features/hud/tuning'
import { showLoading, showMessage, showStartOverlay } from '../hud/overlays'
import { GAME_TITLE } from '../ui/config'
import { attachInput } from '../input/devices'
import { initRapier } from '../physics/physics'
import { createCameraRig } from '../render/cameraRig'
import { createDust } from '../render/dust'
import { createEnvironment } from '../render/environment'
import { FACTION_IDS } from '../render/contracts'
import { buildPlumeWarmHolder, createMaterialLibrary } from '../render/materials'
import { loadMaterialsData, tierOf } from '../render/materials/data'
import { createPostPipeline } from '../render/post'
import { warmPipelines } from '../render/reachFx/warmup'
import { countPipelineBuilds } from '../render/pipelineCount'
import { enableSceneLayers } from '../render/layers'
import { createPassStats, pipelinesLive } from '../render/passStats'
import { backendName, createRenderer, GPU_POWER_PREFERENCE, gpuName, shortGpuName } from '../render/renderer'
import { applyQuality, loadQualityTable, parseQualityOverride } from '../render/quality'
import { buildStandInWarmHolder, createShipViews } from '../render/shipViews'
import { browserStorage, loadSettings } from '../settings/settings'
import { addKitSlots } from '../ships/kits'
import { createSim, rebuildFieldColliders, spawnShip } from '../sim'
import { derelictPose } from '../world/field'
import type { ReachRuntimeOptions } from '../reach/runtime'
import type { ReachRuntime, StorageLike } from '../reach/runtime/types'
import { createFireHold } from './combatInput'
import { isBootAsset, loadAppConfig, parseGameMode, pixelRatioFor, runsInReach, simSystemsFor, withCareerInReachOverride } from './config'
import { createGameBus, DEFAULT_SETTINGS, type GameContext, type GameFeature } from './feature'
import { createFeatureHost, discoverFeatures, initFeatures } from './features'
import { createGameLoop, type GameLoop } from './loop'
import type { CareerReachFactory } from '../career/flow'
import type { ReachBridge, ReachPortDeps } from './mode'
import { createServices } from './services'
import { createUiState } from './ui'

// Unit tests and automated browsers (Playwright sets navigator.webdriver) never take pointer lock: on Windows Chrome
// clips the real system cursor even when headless, which takes over the user's mouse during e2e runs.
const IS_TEST = import.meta.env.MODE === 'test'
const NO_POINTER_LOCK = IS_TEST || (typeof navigator !== 'undefined' && navigator.webdriver)
const IS_DEV = import.meta.env.DEV
const START_SUBTITLE = 'Leased light utility · Reach sandbox'

function getElement<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id)
  if (!el) throw new Error(`#${id} missing from index.html`)
  return el as T
}

/** The fx feature's live particle count, when that feature is present (read by the F3 overlay). */
function particleStatsOf(features: readonly GameFeature[]): { readonly particles: number } | null {
  const fx: unknown = features.find((f) => f.name === 'fx')
  if (typeof fx !== 'object' || fx === null || !('stats' in fx)) return null
  const stats: unknown = fx.stats
  return typeof stats === 'object' && stats !== null && 'particles' in stats && typeof stats.particles === 'number'
    ? (stats as { readonly particles: number })
    : null
}

function hasWebGL2(): boolean {
  return document.createElement('canvas').getContext('webgl2') !== null
}

/** `localStorage` for the Reach save, or null where it is blocked (private modes, sandboxed frames). */
function reachStorage(): StorageLike | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch (err: unknown) {
    logger.warn('localStorage is unavailable: the Reach cannot save', err)
    return null
  }
}

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err)
}

/** Boots the game. Resolves once the loop runs (or an error screen is showing); rejects on unexpected errors. */
export async function boot(onFatal: (err: unknown) => void): Promise<void> {
  if (!('gpu' in navigator) && !hasWebGL2()) {
    showMessage('Unsupported browser', `${GAME_TITLE} needs WebGPU or WebGL2. Use a current Chrome, Edge, Firefox or Safari.`)
    return
  }

  // M10 P4b4c: every kit def slot, before anything sizes a table by the defs (sim, M2 data, career, views).
  const data = loadGameData()
  const baseDefs = addKitSlots(data.ships)
  // R22l: test builds take `?careerInReach=1|0` over app.json (the bridge e2e runs the career in the Reach).
  const config = IS_TEST ? withCareerInReachOverride(loadAppConfig(), location.search) : loadAppConfig()
  const settings = { ...DEFAULT_SETTINGS }
  // The menu feature owns settings; reading the saved preset here builds everything once at that quality.
  const quality = parseQualityOverride(location.search, loadQualityTable()) ?? loadSettings(browserStorage()).quality
  settings.quality = quality
  // M2: `?mode=career|sandbox`, else app.json `defaultMode`. Career turns off the M1 waves and respawn (its
  // encounter director and death flow replace them); sandbox is M1 exactly. R22d: with `careerInReach` on, the
  // career runs in the Reach and `?mode=reach` parses to `career`.
  const mode = parseGameMode(location.search, config)
  const inReach = runsInReach(mode, config)
  document.body.dataset.mode = mode
  logger.info(`Game mode: ${mode}${inReach && mode !== 'reach' ? ' in the Reach' : ''}`)

  try {
    await initRapier()
  } catch (err: unknown) {
    logger.error('Rapier failed to load', err)
    showMessage('Physics failed to load', 'The physics engine could not start.', { label: 'Retry', onClick: () => location.reload() })
    return
  }

  // Renderer. The post pipeline owns sizing from here on (plan §2.2).
  const canvas = getElement<HTMLCanvasElement>('game')
  const query = new URLSearchParams(location.search)
  const forceWebGL = query.get('renderer') === 'webgl2'
  // FG-2 (D-982): test builds can flip canvas.json's opaque WebGPU canvas (`?opaqueCanvas=0|1`) for parity pairs.
  const opaqueParam = IS_TEST ? query.get('opaqueCanvas') : null
  const renderer = await createRenderer(canvas, forceWebGL, opaqueParam === null ? undefined : opaqueParam !== '0')
  const backend = backendName(renderer)
  logger.info(`Renderer backend: ${backend}`)
  // PERF L-E1 (D-802): which GPU runs the game (the high-performance request may be ignored); read once, shown on F3.
  const gpu = gpuName(renderer)
  logger.info(`GPU: ${gpu} (${backend}, asked for ${GPU_POWER_PREFERENCE})`)
  // Test builds count pipelines built (`__debug.pipelinesBuilt`, the D-534 first-thrust check); before any warm-up.
  const pipelinesBuilt = IS_TEST ? countPipelineBuilds(renderer.backend) : null
  let loop: GameLoop | null = null
  renderer.onDeviceLost = (info): void => {
    logger.error('Graphics device lost', info)
    loop?.stop()
    showMessage('Graphics device lost', 'The GPU connection was reset.', { label: 'Reload', onClick: () => location.reload() })
  }

  // Scene: the camera stays at the render origin; everything is placed relative to it (D-011).
  const scene = new Scene()
  const env = await createEnvironment(renderer, scene, { seed: data.world.seed }, quality)
  // The environment says how deep the scene is (sky sphere, impostors).
  const camera = new PerspectiveCamera(data.world.camera.fovDeg, window.innerWidth / window.innerHeight, data.world.camera.near, env.far)
  // PERF PF-0: the main camera draws the scene layer bits (src/render/layers.ts); test builds split each frame's draws and
  // triangles per pass (`__debug.renderPasses`, src/render/passStats.ts).
  enableSceneLayers(camera)
  const passStats = IS_TEST ? createPassStats(renderer, scene, camera) : null

  // Assets. Station and prop models (app.json `deferredAssetPrefixes`) load after the first frame, in career mode
  // only, through `ctx.loader` (M2 plan §2.4, D-TBD). M10 P1b1: ship models boot skips (`ship-stream.json`; none
  // while its flag is off, except `streamedShips`) load through the ship stream.
  const shipStreamConfig = loadShipStreamConfig()
  const bootAsset = (id: string): boolean => isBootAsset(config, shipStreamConfig, id, mode)
  showLoading(0, countAssets(data.assets, bootAsset), 'starting')
  // L-M4 U1 (D-924): the boot preset's materials.json dequantize switch decides how quantized GLBs load.
  const loader = createAssetLoader(renderer, { dequantize: tierOf(loadMaterialsData(), quality).dequantize })
  let assets: Awaited<ReturnType<typeof loadAllAssets>>
  try {
    assets = await loadAllAssets(loader, data.assets, (done, total, id) => showLoading(done, total, id), bootAsset)
  } catch (err: unknown) {
    if (!(err instanceof AssetLoadError)) throw err
    logger.error(err.message, err.cause)
    showMessage(`Asset ${err.assetId} failed to load`, err.message, { label: 'Retry', onClick: () => location.reload() })
    return
  }
  // M10 P1b1: the ship stream, with boot's models preloaded (provided as the `ships` service below).
  const ships = createShipStream({ loader, assets: data.assets, preloaded: assets.models, isStreamed: (id) => id.startsWith('ship.') && !bootAsset(id) })

  const lib = createMaterialLibrary(renderer, assets.textureSets, quality)
  const post = createPostPipeline(renderer, scene, camera, quality)
  post.setSize(window.innerWidth, window.innerHeight, pixelRatioFor(config, quality, window.devicePixelRatio))

  // Simulation (on the same live data the dev panel tunes), its field colliders and the player. The Reach has
  // no preset field (the runtime streams `sim.field`) and no wave director, but AI pilots.
  const sim = inReach
    ? createSim(data.ships, { data, systems: simSystemsFor(mode, true) })
    : createSim(data.ships, { data, preset: quality, systems: simSystemsFor(mode, false) })
  // M10 P1b2: a ship whose model streams collides as its stand-in's bounds box until it lands (the binder swaps it).
  for (let i = 0; i < data.ships.length; i++) sim.shipColliders[i] = ships.colliderOf(data.ships[i].assetId)
  const [sx, sy, sz] = data.world.spawn.position
  spawnShip(sim, 0, sx, sy, sz, true, { faOn: settings.defaultFA })

  const dust = createDust(data.world.seed, data.world.dust.count, data.world.dust.box)
  scene.add(dust.points)
  const views = createShipViews({
    lib,
    models: assets.models,
    // M10 P1b2: a streamed ship shows its stand-in until its GLB lands; the landing bumps `generation` and swaps it.
    source: {
      get: (id) => ships.getOrStandIn(id),
      get generation(): number {
        return ships.generation
      },
    },
    defs: data.ships,
    parent: scene,
    lodScale: config.quality[quality].lodBias,
    hitFullStrength: config.views.hitFullStrength,
  })
  const input = attachInput(canvas, data.bindings, { requirePointerLock: !NO_POINTER_LOCK, onPadDisconnect: () => {} })
  const overlayRoot = getElement('overlay')

  // The service registry exists before the Reach: the career in the Reach's ports find M2's `m2` service in it once
  // the career feature provides it.
  const services = createServices()
  services.provide('ships', ships)

  // The Reach runtime (its own chunk). It enters the initial system and places the player before it resolves. The
  // career in the Reach adds the bridge's ports (M2's career and encounter director, bound once `m2` exists).
  let reach: ReachRuntime | null = null
  // M4 (plan I2): the strategic link's feature (the Reach clock gate), in the Reach only.
  let strategicFeature: GameFeature | null = null
  // M4 (plan I5): the added strategic stations and construction sites, next to the strategic feature.
  let strategicStationsFeature: GameFeature | null = null
  if (inReach) {
    try {
      const career = mode === 'career'
      const [{ createReachRuntime, searchWithGalaxy }, seam, saves] = await Promise.all([
        import('../reach/runtime'),
        career ? import('./mode') : null,
        career ? import('../bridge/m2/saves') : null,
      ])
      const bridge: ReachBridge | null = seam?.createReachBridge({ services, waves: data.waves }) ?? null
      const ports: ReachPortDeps = bridge === null ? {} : { career: bridge.career, encounters: bridge.encounters }
      // R22k: the career's saves carry the Reach state (M2's IndexedDB); localStorage keeps only the pre-bridge save.
      const storage = reachStorage()
      const wiring =
        saves?.createCareerReachWiring({
          storage,
          reload: (seed) => location.assign(`${location.pathname}${searchWithGalaxy(location.search, seed)}`),
        }) ?? null
      const deps: ReachRuntimeOptions & ReachPortDeps = {
        sim,
        data,
        renderer,
        scene,
        camera,
        env,
        lib,
        loader,
        assets: data.assets,
        overlayRoot,
        search: location.search,
        storage,
        reachStorage: wiring?.reachStorage,
        ...ports,
      }
      const runtime = await createReachRuntime(deps)
      bridge?.attach(runtime)
      // R22e: M2's stations follow the entered system (the `m2-stations` enter hook).
      if (bridge !== null) seam?.installSystemStations({ session: runtime.session, models: runtime.models, target: () => services.maybe('m2') ?? null })
      // M4 (plan I2): the strategic client, before the features (its own chunk). A failure leaves the Reach running.
      let wrapCareerReach: ((f: CareerReachFactory) => CareerReachFactory) | null = null
      try {
        const { bootStrategic } = await import('../strategic-link/service')
        const strategic = await bootStrategic({ services, reach: runtime, search: location.search, dev: IS_DEV || IS_TEST, overlayRoot })
        strategicFeature = strategic.feature
        wrapCareerReach = strategic.wrapCareerReach
        const { createStrategicStationsFeature } = await import('../strategic-link/stationsFeature')
        const stationsFeature = createStrategicStationsFeature({ client: strategic.client })
        strategicStationsFeature = stationsFeature
        services.provide('strategicStations', stationsFeature)
      } catch (err: unknown) {
        logger.error('The strategic core failed to start; the Reach runs without its economy', err)
      }
      if (wiring !== null) services.provide('careerReach', wrapCareerReach?.(wiring.factory(runtime)) ?? wiring.factory(runtime))
      reach = runtime
    } catch (err: unknown) {
      logger.error('The Reach failed to load', err)
      showMessage('The Reach failed to load', messageOf(err), { label: 'Retry', onClick: () => location.reload() })
      return
    }
    camera.far = Math.max(camera.far, reach.cameraFar)
    camera.updateProjectionMatrix()
  }

  const bus = createGameBus()
  const ctx: GameContext = {
    data,
    assets: data.assets,
    sim,
    renderer,
    backend,
    canvas,
    scene,
    camera,
    rig: createCameraRig(),
    lib,
    env,
    post,
    models: assets.models,
    textureSets: assets.textureSets,
    views,
    input,
    hudRoot: getElement('hud'),
    overlayRoot,
    bus,
    settings,
    quality,
    mode,
    holds: new Set(),
    services,
    reach,
    loader,
    ui: createUiState(canvas, !NO_POINTER_LOCK),
    controlFilters: [],
    startOverride: null,
  }

  // A preset change rebuilds the field colliders (plan §2.5). Subscribed before the features, so the field
  // view that rebuilds on the same event finds the sim's new layout. Preset changes come from the pause menu
  // (the game is paused) or the test-build debug API (between frames, never inside a tick). Not in the Reach: it
  // streams its own field.
  if (!inReach) {
    bus.on('quality', (preset) => {
      if (sim.preset === preset) return
      rebuildFieldColliders(sim, preset)
      logger.info(`Field colliders rebuilt for ${preset}: ${sim.field?.count ?? 0} rocks`)
    })
  }

  // Features, then the dev tools and the loop.
  const features = discoverFeatures()
  if (strategicFeature !== null) {
    features.push(strategicFeature)
    if (strategicStationsFeature !== null) features.push(strategicStationsFeature)
    features.sort((a, b) => a.order - b.order || (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
  }
  await initFeatures(features, ctx)
  // M4 (plan I4, seams S-M2-5 and S-M2-9): traffic docks at M2's bays and spawns off-screen wrecks once M2 exists.
  // M10 P1b1: traffic requests its ships' models from the stream, outside the M2 guard (pure `reach` has traffic, no M2).
  if (strategicFeature !== null) {
    const { bindTrafficPorts } = await import('../traffic/subsystem')
    bindTrafficPorts({ ships, kits: views }) // M10 P4b4a: promoted Mutual and Wrecker ships wear their kits
    const m2 = services.maybe('m2')
    if (m2 !== undefined) {
      const { createStationBayEnds, createStationBays, createStationKeepOut } = await import('../traffic/bays')
      const { WRECK_CLEAR_M } = await import('../ai/traffic')
      const bayEnds = createStationBayEnds(m2.space.stations, data.assets.models, m2.data.docking)
      const keepOut = createStationKeepOut(m2.space.stations, WRECK_CLEAR_M)
      bindTrafficPorts({ bays: createStationBays(m2.space.stations), docking: m2.data.docking, salvage: m2.space.salvage, bayEnds, keepOut })
    }
  }
  const host = createFeatureHost(features)
  const perf = createPerfOverlay(ctx.services)
  perf.labels.gpu = shortGpuName(gpu)
  // PERF G1: the F3 GPU-ms line (dev.json `perfGpu`): timestamp queries only while F3 is open.
  const perfGpu = loadDevConfig().perfGpu
  if (perfGpu.enabled) perf.gpu = createGpuTiming(renderer, scene, camera, perfGpu)
  const hold = createFireHold()
  const simControl: SimControl | null = IS_DEV || IS_TEST ? createSimControl(sim) : null
  let devPanel: { toggle(): void } | null = null
  if (IS_DEV && simControl) {
    const { createDevPanel } = await import('../dev/devPanel')
    devPanel = createDevPanel({
      ships: data.ships.slice(0, baseDefs),
      weapons: data.weapons,
      ai: data.aiProfiles,
      waves: data.waves,
      json: { Camera: cameraTuning, HUD: hudTuning },
      // After initFeatures, so groups features add to the reach runtime are included.
      groups: [lib.tuning, post.tuning, env.tuning, ...(reach?.tuning ?? [])],
      m2: ctx.services.maybe('m2')?.data ?? null,
    })
    devPanel = (await import('../dev/reachPanel')).withReachFolder(devPanel, ctx.services, IS_DEV) // M4 (plan I8): the Reach folder
    devPanel = (await import('../dev/charterPanel')).withCharterFolder(devPanel, ctx.services, IS_DEV) // M5 (plan C20): the Charter folder
    devPanel = (await import('../dev/fleetPanel')).withFleetFolder(devPanel, ctx.services, IS_DEV) // M6 (plan P36): the Fleet folder
    devPanel = (await import('../dev/storyPanel')).withStoryFolder(devPanel, ctx.services, IS_DEV) // M7 (plan S36): the Story folder
    const { createSpawnMenu } = await import('../dev/spawnMenu')
    createSpawnMenu(simControl, ctx.services)
    // M10 P4b7a: `?perfScene=p4b7` stages the perf check's scene for the user's F3 reading (dev.json `perfScene`).
    const { perfSceneRequested } = await import('../dev/perfScene')
    const perfScene = perfSceneRequested(location.search, loadDevConfig().perfScene)
    if (perfScene) (await import('./debug')).installPerfScene({ sim, control: simControl, services: ctx.services, rig: ctx.rig, cfg: perfScene })
  }
  // Engine plumes stay hidden until a ship thrusts, so the first thrust built their pipelines mid-frame (a 0.45–0.78 s
  // stall, D-TBD 2026-10-05). Compile them in the real render contexts while the start screen renders; every mode.
  const plumes = buildPlumeWarmHolder(lib, FACTION_IDS)
  void warmPipelines(renderer, scene, camera, plumes).then((contexts) => logger.info(`boot: ${plumes.children.length} engine plume parts warmed in ${contexts} render contexts`))
  // M10 P1b2: one stand-in per faction look too, so the first ship that shows one builds no pipeline (D-613/D-616).
  const standInEntry = data.assets.models.find((m) => m.id === data.ships[0].assetId)
  if (standInEntry !== undefined) {
    const standIns = buildStandInWarmHolder(lib, standInModel(standInEntry), FACTION_IDS, config.quality[quality].lodBias)
    void warmPipelines(renderer, scene, camera, standIns).then((contexts) => logger.info(`boot: ${standIns.children.length} stand-in looks warmed in ${contexts} render contexts`))
  }
  loop = createGameLoop({
    ctx,
    host,
    config,
    dust,
    perf,
    devPanel,
    requirePointerLock: !NO_POINTER_LOCK,
    hold,
    simControl,
    particleStats: particleStatsOf(features),
  })
  const running = loop

  // Uncaught errors stop the loop instead of running broken (spec §12).
  window.addEventListener('error', (e) => {
    running.stop()
    onFatal(e.error ?? e.message)
  })
  window.addEventListener('unhandledrejection', (e) => {
    running.stop()
    onFatal(e.reason)
  })

  if (IS_TEST && simControl && pipelinesBuilt) {
    const { createDebugApi } = await import('./debug')
    window.__debug = createDebugApi({
      backend,
      sim,
      control: simControl,
      hold,
      paused: () => running.pause.paused,
      setQuality: (preset) => {
        applyQuality(ctx, preset)
        passStats?.resolve()
      },
      pipelinesBuilt,
      renderInfo: renderer.info,
      passStats: passStats ?? undefined,
      pipelinesLive: () => pipelinesLive(renderer),
      views,
      scene,
      fieldB: mode === 'sandbox' ? { harrow: derelictPose(ctx.data.field).position, rig: ctx.rig, sun: ctx.env.sun } : undefined,
      services: ctx.services,
      mode,
      joystick: input.joystick,
    })
  }

  // A feature may replace "Click to fly" (M2: the career title screen); `begin` must run inside a user gesture.
  const begin = (): void => running.pause.start()
  if (ctx.startOverride) ctx.startOverride(begin)
  else showStartOverlay(reach?.subtitle ?? START_SUBTITLE, begin)
  await running.run()
}
